import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { motion } from "framer-motion";
import { trackEvent } from "../utils/analytics";

// --- CONFIGURATION ---
const MEMBERSHIP_PLANS = [
  {
    name: "Starter Membership",
    price: "$49",
    period: "/ mo",
    description: "For solo operators and smaller teams with lighter monthly photo volume.",
    subtext: "Billed monthly. Unused Upload Credits roll over.",
    highlight: false,
    features: [
      "250 photo uploads each month",
      "Three active sales",
      "Complete Scucci workflow",
      "Unused Upload Credits roll over",
    ],
  },
  {
    name: "Professional Membership",
    price: "$99",
    period: "/ mo",
    description: "For active estate-sale teams running regular sales and using the full workflow.",
    badge: "Recommended for active teams",
    subtext: "Billed monthly. Unused Upload Credits roll over.",
    highlight: true,
    features: [
      "600 photo uploads each month",
      "Six active sales",
      "Complete Scucci workflow",
      "Unused Upload Credits roll over",
    ],
  },
  {
    name: "Business Membership",
    price: "$179",
    period: "/ mo",
    description: "For companies preparing several sales at once or processing larger photo volumes across their team.",
    subtext: "Billed monthly. Unused Upload Credits roll over.",
    highlight: false,
    features: [
      "1,250 photo uploads each month",
      "Twelve active sales",
      "Complete Scucci workflow",
      "Unused Upload Credits roll over",
    ],
  },
];

const CREDIT_PACK_ROWS = [
  { size: "25 Upload Credits", starter: "$7.50", pro: "$5.50", business: "Not offered" },
  { size: "50 Upload Credits", starter: "$15.00", pro: "$11.00", business: "$8.00" },
  { size: "100 Upload Credits", starter: "$30.00", pro: "$22.00", business: "$16.00" },
  { size: "250 Upload Credits", starter: "$75.00", pro: "$55.00", business: "$40.00" },
  { size: "500 Upload Credits", starter: "$150.00", pro: "$110.00", business: "$80.00" },
  { size: "1,000 Upload Credits", starter: "$300.00", pro: "$220.00", business: "$160.00" },
];

const FAQS = [
  {
    question: "What if something in the draft inventory needs to be changed?",
    answer:
      "Scucci creates editable drafts. Review, change, or delete items before printing tags or using them at checkout. Your team controls the final inventory and prices.",
  },
  {
    question: "Do unused Upload Credits expire?",
    answer:
      "Unused credits roll over while your membership remains active. If you cancel, remaining credits stay on your account for two years after your paid membership ends.",
  },
  {
    question: "What happens if I use all my monthly Upload Credits?",
    answer:
      "You can wait for the next monthly allocation, purchase an additional credit pack, or move to a higher-volume membership. Your existing inventory, tags, and sale records remain available.",
  },
  {
    question: "Can I change plans?",
    answer:
      "Yes. Move to a different membership as your photo volume or number of active sales changes.",
  },
  {
    question: "Can I cancel anytime?",
    answer:
      "Yes. Cancel from your dashboard. Your membership remains active through the end of the current billing period and will not renew.",
  },
  {
    question: "Do I have to inventory an entire sale?",
    answer:
      "No. Use Scucci for one room, one category, or only the merchandise you want tagged and tracked. Add uninventoried items manually during checkout.",
  },
  {
    question: "Do I need to change payment processors?",
    answer:
      "No. Continue accepting cash or using your current card processor and record those sales in Scucci. Connected Stripe accounts can also create customer payment QR codes.",
  },
  {
    question: "Do I need special printing equipment?",
    answer:
      "No. Scucci creates supported sheet-label layouts you can print with a standard home or office printer, either before the sale or on-site. Optional thermal-label workflows are also available.",
  },
  {
    question: "Can I download my inventory and sales records?",
    answer:
      "Yes. Export your inventory and available sale data to CSV for backups, staff use, client records, or other software workflows.",
  },
  {
    question: "Is Scucci a point-of-sale system?",
    answer:
      "Scucci supports estate-sale checkout by helping staff build carts, apply discounts and tax, record payments, and track sold items. You can continue processing cards through your current provider or use supported Scucci payment options when connected.",
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const PUBLIC = process.env.PUBLIC_URL || "";
  const [isScrolled, setIsScrolled] = useState(false);
  const isSignedIn = !!user;
  const trackNav = (to, placement) => {
    const suffix = placement ? `__${placement}` : "";
    trackEvent(`nav_pricing_to_${to}${suffix}`);
  };

  // Navigation Helpers
  const goToSignIn = () => navigate("/signin");
  const goToAbout = () => {
    trackNav("about", "header");
    navigate("/about");
  };
  const goPrimary = () => {
    if (isSignedIn) return navigate("/estate-sales-pipeline");
    trackNav("register", "header");
    return navigate("/register");
  };
  const primaryLabel = isSignedIn ? "Go to Dashboard" : "Test Scucci Free";
  const headerLabel = isSignedIn ? "Go to Dashboard" : "Test Scucci Free";
  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const visiblePlans = MEMBERSHIP_PLANS;
  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // --- BLOG COVER LOGIC ---

  const [showCreditPacks, setShowCreditPacks] = useState(false);
  const [showAllFaqs, setShowAllFaqs] = useState(false);

  return (
    <div className="w-full min-h-screen bg-white font-sans text-slate-900 flex flex-col">
      
      {/* --- HEADER (match Welcome page) --- */}
      <header className="fixed top-0 inset-x-0 z-50">
        <div
          className={
            isScrolled
              ? "absolute inset-0 bg-white/90 backdrop-blur border-b border-gray-200"
              : "absolute inset-0 bg-blue-950/55 backdrop-blur"
          }
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              onClick={() => trackNav("home", "header")}
              className={
                isScrolled
                  ? "text-blue-950 text-lg font-extrabold tracking-wider"
                  : "text-white text-lg font-extrabold tracking-wider"
              }
              aria-label="Scucci Systems"
            >
              Scucci Systems
            </Link>
            <span
              className={
                isScrolled
                  ? "hidden md:inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-950 border border-blue-100"
                  : "hidden md:inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white/90"
              }
            >
              Estate Sales
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                trackNav("home", "header");
                navigate("/");
              }}
              className={
                isScrolled
                  ? "px-3 sm:px-4 py-2 border border-gray-200 text-blue-950 text-xs sm:text-sm font-semibold rounded-xl hover:bg-gray-50 transition"
                  : "px-3 sm:px-4 py-2 border border-white/20 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-white/10 transition"
              }
            >
              Home
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={goToAbout}
              className={
                isScrolled
                  ? "px-3 sm:px-4 py-2 border border-gray-200 text-blue-950 text-xs sm:text-sm font-semibold rounded-xl hover:bg-gray-50 transition"
                  : "px-3 sm:px-4 py-2 border border-white/20 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-white/10 transition"
              }
            >
              About
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={goPrimary}
              className={
                isScrolled
                  ? "inline-flex items-center gap-2 px-4 sm:px-5 py-2 bg-blue-950 text-white text-xs sm:text-sm font-extrabold rounded-xl shadow hover:shadow-md transition"
                  : "inline-flex items-center gap-2 px-4 sm:px-5 py-2 bg-white text-blue-950 text-xs sm:text-sm font-extrabold rounded-xl shadow hover:shadow-md transition"
              }
            >
              {headerLabel}
            </motion.button>

            {!isSignedIn && (
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                onClick={goToSignIn}
                className={
                  isScrolled
                    ? "px-4 sm:px-5 py-2 border border-gray-200 text-blue-950 text-xs sm:text-sm font-bold rounded-xl hover:bg-gray-50 transition"
                    : "px-4 sm:px-5 py-2 border border-white/25 text-white text-xs sm:text-sm font-bold rounded-xl hover:bg-white/10 transition"
                }
              >
                Sign In
              </motion.button>
            )}
          </div>
        </div>
      </header>

      {/* --- HERO SECTION --- */}
      <div
        className="relative overflow-hidden text-white bg-cover bg-center pt-10"
        style={{ backgroundImage: `url('${PUBLIC}/images/hero/momestate132.jpg')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/85 via-blue-950/75 to-blue-950/90"></div>
        
        <section className="relative z-10 max-w-5xl mx-auto px-6 pt-16 pb-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-extrabold mb-5 leading-tight"
          >
            See what Scucci creates from your own sale photos.
            <span className="block mt-2 text-blue-200/90 text-2xl md:text-4xl leading-snug">
              Test the inventory and tagging workflow before using it on a live sale.
            </span>
          </motion.h1>
          <p className="mt-3 text-base md:text-lg text-blue-100/90 max-w-3xl mx-auto">
            Upload photos from a previous sale, review the editable inventory Scucci creates, and print sample tags. Start free with 25 photo uploads for 30 days. No credit card required.
          </p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <button
              type="button"
              onClick={goPrimary}
              className="px-10 py-3 rounded-xl bg-white text-blue-900 font-extrabold text-base shadow-lg hover:bg-blue-50 transition border border-white/80"
            >
              {isSignedIn ? "Go to Dashboard" : "Start With Previous Sale Photos"}
            </button>
            <button
              type="button"
              onClick={() => scrollToId("plans")}
              className="px-10 py-3 rounded-xl border border-white/30 text-white font-extrabold text-base hover:bg-white/10 transition"
            >
              See Membership Options
            </button>
          </motion.div>
        </section>
      </div>
      

      {/* --- TRUST / PRINTER STRIP --- */}
      <div className="bg-blue-950 text-white/80 py-3 text-center text-sm font-medium relative z-20 border-b border-blue-900">
        Print with a standard home or office printer. Optional thermal-label formats are also available.
      </div>
      
      {/* --- TEST PATH --- */}
      <section className="max-w-5xl mx-auto px-6 py-10 md:py-12">
        <div className="rounded-[28px] border border-blue-100 bg-blue-50/70 px-6 py-8 md:px-8 md:py-10 shadow-sm text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-blue-900">
            Test Scucci without risking a live sale
          </h2>
          <p className="mt-4 text-base md:text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Use photos from work you have already completed and judge the output before changing your current process.
          </p>
          <div className="mt-6 grid md:grid-cols-3 gap-4 text-left">
            <div className="rounded-2xl border border-blue-100 bg-white px-5 py-5 shadow-sm">
              <div className="text-lg font-extrabold text-blue-900">1. Upload previous sale photos</div>
              <div className="mt-2 text-gray-700">Start with a small group of photos your team already has.</div>
            </div>
            <div className="rounded-2xl border border-blue-100 bg-white px-5 py-5 shadow-sm">
              <div className="text-lg font-extrabold text-blue-900">2. Review and edit the inventory</div>
              <div className="mt-2 text-gray-700">Check the identified items, titles, descriptions, categories, and suggested prices. Make any changes you need.</div>
            </div>
            <div className="rounded-2xl border border-blue-100 bg-white px-5 py-5 shadow-sm">
              <div className="text-lg font-extrabold text-blue-900">3. Print sample tags</div>
              <div className="mt-2 text-gray-700">See how the inventory and tagging workflow fits your operation. Then use Scucci for one room or one batch of photos at your next sale.</div>
            </div>
          </div>
          <p className="mt-6 text-base font-semibold text-blue-900">
            Continue with a membership only after the workflow proves useful to your team.
          </p>
        </div>
      </section>

      {/* --- INCLUDED FEATURES GRID --- */}
      <section className="max-w-6xl mx-auto px-6 mb-20">
        <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-blue-900">What every membership includes</h2>
            <p className="mt-4 text-base md:text-lg text-gray-700 max-w-3xl mx-auto">
              Create the inventory once, then use it to print tags, keep prices available to staff, move checkout, and retain a record of what sold.
            </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition">
                <h3 className="font-bold text-lg text-blue-900 mb-2">Create inventory with less typing</h3>
                <p className="text-gray-600 text-sm">Upload ordinary sale photos and receive editable draft items with titles, descriptions, categories, and suggested prices.</p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition">
                <h3 className="font-bold text-lg text-blue-900 mb-2">Print the tags you need in batches</h3>
                <p className="text-gray-600 text-sm">Review your inventory and print the tags you need using supported sheet-label or thermal-label workflows.</p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition">
                <h3 className="font-bold text-lg text-blue-900 mb-2">Keep prices available to the team</h3>
                <p className="text-gray-600 text-sm">Staff can search inventory from a phone or computer when a tag is missing or a price needs to be verified.</p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition">
                <h3 className="font-bold text-lg text-blue-900 mb-2">Keep checkout moving</h3>
                <p className="text-gray-600 text-sm">Use live search or QR scanning to add inventoried items, then manually enter anything that was not prepared in advance.</p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition">
                <h3 className="font-bold text-lg text-blue-900 mb-2">Record what happened at the sale</h3>
                <p className="text-gray-600 text-sm">Track sold items, final prices, discounts, tax, payment method, and what remains.</p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition">
                <h3 className="font-bold text-lg text-blue-900 mb-2">Keep records you can use after the sale</h3>
                <p className="text-gray-600 text-sm">Export inventory and sales data to CSV for client records, team review, backups, or other workflows.</p>
            </div>
        </div>
      </section>

      {/* --- PRICING CARDS --- */}
      <section id="plans" className="max-w-7xl mx-auto px-6 mt-2 relative z-20 mb-16">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-extrabold text-blue-900 mb-4">
            Choose the membership that fits your sales
          </h2>
          <p className="text-sm md:text-base text-gray-700 max-w-2xl mx-auto">
            Every paid plan includes the complete inventory, tagging, checkout, and sales-tracking workflow. Test Scucci free first, then choose based on monthly photo volume and the number of sales your team needs active at once.
          </p>
          {!isSignedIn && (
            <p className="mt-3 text-sm font-semibold text-blue-800">
              Start free with 25 photo uploads for 30 days before choosing a paid membership. No credit card required.
            </p>
          )}
        </div>
        <div className="grid gap-6 items-start lg:grid-cols-3 lg:justify-center">
          {visiblePlans.map((plan, idx) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              viewport={{ once: true }}
              className={`bg-white rounded-2xl shadow-xl p-6 flex flex-col h-full relative border-2 ${
                plan.highlight
                  ? "border-blue-600 ring-4 ring-blue-600/10 scale-105 z-30"
                  : "border-slate-200/80 z-10 opacity-95"
              }`}
            >
              {plan.badge && (
                <div className={`absolute -top-4 left-1/2 -translate-x-1/2 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider shadow-sm ${
                    plan.highlight ? "bg-blue-600" : "bg-green-600"
                }`}>
                  {plan.badge}
                </div>
              )}

              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 mt-2 text-center">{plan.name}</h3>
              
              <div className="text-center mb-4">
                <span className="text-4xl md:text-5xl font-extrabold text-blue-900">{plan.price}</span>
                <span className="text-gray-500 font-medium text-sm">{plan.period}</span>
              </div>
              
              <p className="text-sm md:text-base text-gray-600 mb-4 text-center min-h-[2.5rem]">
                {plan.description}
              </p>

              {/* FEATURES LIST */}
              <ul className="mb-6 space-y-3">
                {plan.features.map((feature, i) => (
                  <li
                    key={i}
                    className={`flex items-start text-sm md:text-base ${
                      plan.highlight ? "text-gray-700" : "text-gray-600"
                    }`}
                  >
                    <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-auto">
                {isSignedIn && (
                  <button
                    type="button"
                    onClick={() => navigate("/estate-sales-pipeline/member-maint2")}
                    className={`w-full py-3 rounded-xl font-bold text-center transition block border shadow-sm hover:shadow-md ${
                      plan.highlight
                        ? "bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
                        : "bg-blue-50 text-blue-900 hover:bg-blue-100 border-blue-300"
                    }`}
                  >
                    Manage Membership
                  </button>
                )}
                <p className="text-xs md:text-sm text-gray-500 mt-4 text-center">
                  {plan.subtext}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
        {!isSignedIn && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={goPrimary}
              className="rounded-xl bg-blue-900 px-8 py-3 font-extrabold text-white shadow-sm transition hover:bg-blue-800"
            >
              Test Scucci Free
            </button>
            <p className="mt-3 text-sm text-gray-600">
              25 photo uploads for 30 days. No credit card required.
            </p>
          </div>
        )}
        <p className="mx-auto mt-8 max-w-4xl text-center text-sm md:text-base font-semibold text-gray-700">
          All paid memberships include inventory creation, editing, unlimited tag printing, checkout tools, sale tracking, and CSV exports.
        </p>
      </section>

      {/* --- CREDIT DEFINITION --- */}
      <section id="credits" className="max-w-4xl mx-auto px-6 mb-20 text-center">
        <div className="bg-blue-50 border border-blue-200 rounded-[28px] p-6 md:p-8 shadow-sm">
          <h2 className="text-2xl md:text-3xl font-extrabold text-blue-900">
            How Upload Credits work
          </h2>
          <div className="mt-5 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 text-lg md:text-2xl font-extrabold text-blue-800">
            <span className="bg-white px-4 py-2 rounded-lg shadow-sm border border-blue-100">1 uploaded photo</span>
            <span className="text-blue-500">→</span>
            <span className="bg-white px-4 py-2 rounded-lg shadow-sm border border-blue-100">1 Upload Credit</span>
          </div>
          <p className="mt-5 text-blue-900/80 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
            One photo uses one Upload Credit and can create multiple editable items. Upload Credits are used only when you submit photos for inventory creation.
          </p>
          <p className="text-blue-900/70 text-sm md:text-base mt-3 max-w-3xl mx-auto">
            Unused credits roll over while your membership remains active. Editing inventory, printing tags, searching at checkout, recording sales, and exporting data do not use Upload Credits.
          </p>
        </div>
      </section>

      {/* --- FAQ SECTION --- */}
      <section id="faq" className="max-w-4xl mx-auto px-6 py-12">
        <h2 className="text-3xl font-extrabold text-blue-900 text-center mb-8">Frequently Asked Questions</h2>
        <p className="text-center text-gray-600 mb-8">A few practical questions before you start.</p>
        <div className="grid gap-6">
            {(showAllFaqs
              ? FAQS
              : FAQS.filter((f) =>
                  [
                    "What if something in the draft inventory needs to be changed?",
                    "Do I have to inventory an entire sale?",
                    "Do I need to change payment processors?",
                    "Do I need special printing equipment?",
                    "Can I cancel anytime?",
                  ].includes(f.question)
                )
            ).map((faq, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition">
                    <h4 className="font-bold text-lg text-blue-900 mb-2">{faq.question}</h4>
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
            ))}
        </div>
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => setShowAllFaqs(!showAllFaqs)}
            className="text-sm font-semibold text-blue-900 underline underline-offset-4 hover:text-blue-700"
          >
            {showAllFaqs ? "Hide extra FAQs" : "Show all FAQs"}
          </button>
        </div>
      </section>

      {/* --- CREDIT PACK TABLE --- */}
      <section className="bg-slate-100 border-t border-gray-200 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-blue-900 mb-4">
              Have an unusually large sale?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Your monthly Upload Credits roll over. When a larger-than-usual project requires more, active members can purchase additional credit packs without changing plans.
            </p>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowCreditPacks(!showCreditPacks)}
                className="text-sm font-semibold text-blue-900 underline underline-offset-4 hover:text-blue-700"
              >
                {showCreditPacks ? "Hide additional credit packs" : "View Additional Credit Packs"}
              </button>
            </div>
          </div>
          {showCreditPacks && (
            <>
              <div className="overflow-hidden bg-white rounded-xl shadow-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr className="divide-x divide-gray-200">
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-500">Pack Size</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Starter</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-50">Professional</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Business</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 text-sm">
                    {CREDIT_PACK_ROWS.map((row, idx) => (
                      <tr key={row.size} className={`divide-x divide-gray-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="px-6 py-4 font-bold text-gray-900">{row.size}</td>
                        <td className="px-6 py-4 text-center text-gray-600 font-medium">{row.starter}</td>
                        <td className="px-6 py-4 text-center text-blue-800 font-bold bg-blue-50/50">{row.pro}</td>
                        <td className="px-6 py-4 text-center text-gray-600 font-medium">{row.business}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-center text-sm text-gray-500 mt-4 italic">
                Additional Upload Credit packs are optional and discounted for active members.
              </p>
            </>
          )}
        </div>
      </section>

      {/* --- ASSISTED ONBOARDING --- */}
      <section className="max-w-5xl mx-auto px-6 py-14">
        <div className="rounded-[28px] border border-blue-200 bg-blue-50 px-6 py-8 md:px-10 md:py-10 text-center">
          <h2 className="text-3xl font-extrabold text-blue-900">Prefer help setting up your first test?</h2>
          <p className="mt-4 text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Send us a few details about your current workflow, printer, and previous sale photos. We will help you choose a small first test.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3">
            <a
              href="mailto:support@scuccisystems.com?subject=Help%20with%20my%20first%20Scucci%20test"
              className="inline-flex items-center justify-center rounded-xl bg-blue-900 px-6 py-3 font-extrabold text-white hover:bg-blue-800 transition"
            >
              Get Help With Your First Test
            </a>
            <p className="text-sm font-semibold text-gray-600">A real person will respond at support@scuccisystems.com.</p>
          </div>
        </div>
      </section>

      {/* --- TRUST/FOOTER STRIP --- */}
      <section className="bg-blue-900 py-12 text-center text-white mt-auto">
        <div className="max-w-4xl mx-auto px-6">
            <div className="mb-8">
                <span className="inline-block px-4 py-1 rounded-full bg-blue-800 border border-blue-700 text-xs font-semibold tracking-wide uppercase mb-4">
                    Free product test
                </span>
                <h2 className="text-2xl md:text-3xl font-bold mb-4">
                    Start with work you have already completed
                </h2>
                <p className="text-blue-200 max-w-2xl mx-auto">
                    Test Scucci with photos from a previous sale. Review the editable inventory and print sample tags before using Scucci on your next sale.
                </p>
            </div>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              type="button"
              onClick={goPrimary}
              className="px-8 py-3 rounded-xl bg-white text-blue-900 font-bold text-lg hover:bg-blue-50 transition shadow-lg"
            >
              {primaryLabel}
            </button>
          </div>
          {!isSignedIn && (
            <p className="mt-4 text-blue-300 text-sm">25 photo uploads for 30 days. No credit card required.</p>
          )}
        </div>
      </section>
      
    </div>
  );
}